import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  Frame,
  MessageSquare,
  Upload,
  Check,
  Trash2,
  Plus,
  Loader2,
  Phone,
  User as UserIcon,
  ExternalLink,
  Power,
  Images,
  TrendingUp,
  PanelBottom,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("frames");
  const [stats, setStats] = useState({ total: 0, today: 0, frames: 0, strips: 0 });

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/submissions/stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const headingTitle =
    tab === "frames" ? "Frame Gallery" :
    tab === "strips" ? "Bottom Strips" :
    "Submissions";

  const headingSubtitle =
    tab === "frames" ? "Upload celebrity frames and choose the active one shown to users."
    : tab === "strips" ? "Upload bottom banners (e.g. Happy Birthday) shown under every selfie."
    : "Every wish users send lives here.";

  return (
    <div className="min-h-[100dvh] flex flex-col md:flex-row bg-background" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-[100dvh] md:border-r border-border bg-white md:flex-shrink-0">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs font-bold tracking-widest text-muted-foreground">WISHES</div>
            <div className="text-lg font-black tracking-tight">Admin Studio</div>
          </div>
        </div>
        <nav className="p-4 space-y-1">
          <NavButton
            active={tab === "frames"}
            onClick={() => setTab("frames")}
            icon={<Frame className="h-4 w-4" />}
            label="Frames"
            testid="nav-frames"
          />
          <NavButton
            active={tab === "strips"}
            onClick={() => setTab("strips")}
            icon={<PanelBottom className="h-4 w-4" />}
            label="Bottom Strips"
            testid="nav-strips"
          />
          <NavButton
            active={tab === "submissions"}
            onClick={() => setTab("submissions")}
            icon={<MessageSquare className="h-4 w-4" />}
            label="Submissions"
            testid="nav-submissions"
          />
        </nav>
        <div className="p-4 mt-auto md:absolute md:bottom-0 md:w-64 border-t border-border bg-white">
          <div className="text-xs text-muted-foreground mb-3 px-2 truncate">{admin?.email}</div>
          <div className="flex gap-2">
            <Link
              to="/"
              target="_blank"
              className="flex-1 h-10 rounded-full flex items-center justify-center gap-1 text-xs font-semibold bg-muted hover:bg-muted/70 transition"
              data-testid="view-site-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View site
            </Link>
            <Button
              onClick={logout}
              variant="outline"
              className="h-10 rounded-full text-xs font-semibold"
              data-testid="logout-btn"
            >
              <LogOut className="h-3.5 w-3.5 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        <header className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight">{headingTitle}</h1>
            <p className="text-muted-foreground text-sm mt-1">{headingSubtitle}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <StatPill label="Total wishes" value={stats.total} icon={<Images className="h-4 w-4" />} />
            <StatPill label="Today" value={stats.today} icon={<TrendingUp className="h-4 w-4" />} />
            <StatPill label="Frames" value={stats.frames} icon={<Frame className="h-4 w-4" />} />
            <StatPill label="Strips" value={stats.strips || 0} icon={<PanelBottom className="h-4 w-4" />} />
          </div>
        </header>

        {tab === "frames" && (
          <AssetPanel
            kind="frame"
            apiBase="/frames"
            title="Your frames"
            emptyLabel="No frames yet"
            emptyHint="Upload your first celebrity frame above."
            uploadTitle="Upload a new frame"
            uploadHint="PNG with transparent background works best. Max 10MB."
            namePlaceholder="Frame name (e.g. Birthday 2026)"
            cardAspect="aspect-square"
            imgClass="object-contain"
            onChange={loadStats}
          />
        )}
        {tab === "strips" && (
          <AssetPanel
            kind="strip"
            apiBase="/strips"
            title="Your bottom strips"
            emptyLabel="No strips yet"
            emptyHint="Upload a Happy Birthday banner or any bottom strip design."
            uploadTitle="Upload a new bottom strip"
            uploadHint="Wide banner image (e.g. 1200×300). Stretches to full width, aspect-preserved. Max 10MB."
            namePlaceholder="Strip name (e.g. Happy Birthday)"
            cardAspect="aspect-[3/1]"
            imgClass="object-contain"
            onChange={loadStats}
          />
        )}
        {tab === "submissions" && <SubmissionsPanel onChange={loadStats} />}
      </main>
    </div>
  );
}

function NavButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full px-4 py-3 rounded-2xl flex items-center gap-3 font-semibold text-sm transition ${
        active
          ? "bg-[#0C0C0C] text-white"
          : "text-foreground hover:bg-muted"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function StatPill({ label, value, icon }) {
  return (
    <div className="h-14 px-5 rounded-2xl bg-white border border-border flex items-center gap-3">
      <div className="h-8 w-8 rounded-xl bg-[#CCFF00] flex items-center justify-center text-[#0C0C0C]">
        {icon}
      </div>
      <div>
        <div className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">{label}</div>
        <div className="text-lg font-black leading-none">{value}</div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Generic asset panel — used for both Frames and Strips
// -----------------------------------------------------------------------------
function AssetPanel({
  kind,
  apiBase,
  title,
  emptyLabel,
  emptyHint,
  uploadTitle,
  uploadHint,
  namePlaceholder,
  cardAspect,
  imgClass,
  onChange,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [itemName, setItemName] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(apiBase);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!itemName.trim()) {
      toast.error(`Enter a ${kind} name first`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", itemName.trim());
    try {
      await api.post(`${apiBase}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${labelCap(kind)} uploaded`);
      setItemName("");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const activate = async (id) => {
    try {
      await api.post(`${apiBase}/${id}/activate`);
      toast.success(`${labelCap(kind)} is now live`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deactivateAll = async () => {
    try {
      await api.post(`${apiBase}/deactivate-all`);
      toast.success(`No ${kind} is active`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    try {
      await api.delete(`${apiBase}/${id}`);
      toast.success("Deleted");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      {/* Upload zone */}
      <div className="bg-white rounded-3xl border border-border p-6" data-testid={`${kind}-upload-card`}>
        <h2 className="text-xl font-bold mb-1">{uploadTitle}</h2>
        <p className="text-sm text-muted-foreground mb-5">{uploadHint}</p>
        <div className="grid md:grid-cols-[1fr,auto] gap-3">
          <Input
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder={namePlaceholder}
            className="h-12 rounded-2xl focus-visible:ring-[#FF4D4D]"
            data-testid={`${kind}-name-input`}
          />
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleUpload}
              className="hidden"
              data-testid={`${kind}-file-input`}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`upload-${kind}-btn`}
              className="h-12 px-6 rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white font-bold"
            >
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Pick file
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Items grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          {items.some((i) => i.is_active) && (
            <Button
              onClick={deactivateAll}
              variant="outline"
              className="h-10 rounded-full text-xs"
              data-testid={`deactivate-all-${kind}-btn`}
            >
              <Power className="h-3.5 w-3.5 mr-1" />
              Deactivate all
            </Button>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-3xl border border-dashed border-border">
            <Plus className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
            <p className="font-semibold">{emptyLabel}</p>
            <p className="text-sm text-muted-foreground">{emptyHint}</p>
          </div>
        ) : (
          <div
            className={`grid gap-4 ${
              kind === "strip" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            }`}
            data-testid={`${kind}s-grid`}
          >
            {items.map((f) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`group relative rounded-3xl overflow-hidden bg-white border-2 transition ${
                  f.is_active ? "border-[#FF4D4D] shadow-lg" : "border-border hover:border-[#0C0C0C]"
                }`}
                data-testid={`${kind}-card-${f.id}`}
              >
                <div className={`${cardAspect} bg-gradient-to-br from-[#FFE5E5] to-[#F5FFD0] relative`}>
                  <img
                    src={fileUrl(f.storage_path)}
                    alt={f.name}
                    className={`absolute inset-0 w-full h-full ${imgClass}`}
                  />
                  {f.is_active && (
                    <div className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-[#FF4D4D] text-white text-[10px] font-bold tracking-widest">
                      LIVE
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-bold text-sm truncate mb-2">{f.name}</div>
                  <div className="flex gap-1.5">
                    {!f.is_active && (
                      <Button
                        onClick={() => activate(f.id)}
                        className="flex-1 h-9 rounded-full bg-[#0C0C0C] hover:bg-black text-white text-xs"
                        data-testid={`activate-${kind}-${f.id}`}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Activate
                      </Button>
                    )}
                    <Button
                      onClick={() => remove(f.id)}
                      variant="outline"
                      className="h-9 rounded-full text-xs"
                      data-testid={`delete-${kind}-${f.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function labelCap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

// -----------------------------------------------------------------------------
// Submissions
// -----------------------------------------------------------------------------
function SubmissionsPanel({ onChange }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/submissions");
      setSubmissions(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this wish?")) return;
    try {
      await api.delete(`/submissions/${id}`);
      toast.success("Deleted");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="py-16 text-center bg-white rounded-3xl border border-dashed border-border">
        <MessageSquare className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
        <p className="font-semibold">No wishes yet</p>
        <p className="text-sm text-muted-foreground">When users submit, they'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="submissions-grid">
      {submissions.map((s) => (
        <motion.div
          key={s.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="group relative rounded-3xl overflow-hidden bg-white border border-border hover:-translate-y-1 hover:shadow-xl transition-all"
          data-testid={`submission-card-${s.id}`}
        >
          <div className="relative overflow-hidden bg-muted">
            <img
              src={fileUrl(s.image_path)}
              alt={s.name}
              className="w-full h-auto block"
            />
            <button
              onClick={() => remove(s.id)}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:bg-[#FF4D4D]"
              data-testid={`delete-submission-${s.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-sm leading-relaxed mb-3 line-clamp-3">"{s.wish}"</p>
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {s.name}
              </div>
              <a
                href={`tel:${s.phone}`}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#FF4D4D] transition"
              >
                <Phone className="h-3.5 w-3.5" />
                {s.phone}
              </a>
            </div>
            <div className="text-[10px] text-muted-foreground mt-2">
              {new Date(s.created_at).toLocaleString()}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
